import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Named zones of the floor plan. Only rendered where the tenant has the
 * tableAreasEnabled setting on: every /api/table-areas route is behind that
 * toggle and answers 403 when it is off.
 */
function TableAreasManagement({ theme, onAreasChanged }) {
    const { t } = useLanguage();
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newAreaName, setNewAreaName] = useState('');
    const [error, setError] = useState('');

    const fetchAreas = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BACKEND_URL}/api/table-areas`);
            if (!response.ok) throw new Error(t('failedToLoadAreas'));
            setAreas(await response.json());
            setError('');
        } catch (err) {
            console.error('Error fetching table areas:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchAreas();
    }, [fetchAreas]);

    const notifyChanged = () => {
        fetchAreas();
        if (onAreasChanged) onAreasChanged();
    };

    const handleAddArea = async () => {
        if (!newAreaName.trim()) return;
        setSaving(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/table-areas`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newAreaName.trim() })
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || t('failedToSaveArea'));
            }
            setNewAreaName('');
            notifyChanged();
        } catch (err) {
            console.error('Error creating table area:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleRenameArea = async (area, name) => {
        if (!name.trim() || name === area.name) return;
        try {
            const response = await fetch(`${BACKEND_URL}/api/table-areas/${area.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: name.trim() })
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || t('failedToSaveArea'));
            }
            notifyChanged();
        } catch (err) {
            console.error('Error renaming table area:', err);
            setError(err.message);
        }
    };

    const handleDeleteArea = async (area) => {
        if (!window.confirm(`${t('confirmDeleteArea')} ${area.name}?`)) return;
        try {
            const response = await fetch(`${BACKEND_URL}/api/table-areas/${area.id}`, {
                method: 'DELETE'
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || t('failedToDeleteArea'));
            }
            notifyChanged();
        } catch (err) {
            console.error('Error deleting table area:', err);
            setError(err.message);
        }
    };

    return (
        <div style={{
            marginBottom: '0.75rem',
            padding: '0.5rem 0.75rem',
            background: theme?.colors?.background || '#f5f5f5',
            borderRadius: theme?.borderRadius || 8,
            border: `1px solid ${theme?.colors?.border || '#ddd'}`
        }}>
            <div style={{
                fontWeight: 'bold',
                color: theme?.colors?.primary || '#1976d2',
                fontSize: '0.85rem',
                marginBottom: '0.5rem'
            }}>
                {t('tableAreas')}
            </div>

            {loading ? (
                <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
                    {t('loading')}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {areas.map(area => (
                        <div key={area.id} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                defaultValue={area.name}
                                maxLength={100}
                                onBlur={(e) => handleRenameArea(area, e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                style={{
                                    flex: '1 1 140px',
                                    minWidth: 0,
                                    padding: '0.3rem 0.4rem',
                                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                                    borderRadius: theme?.borderRadius || 4,
                                    fontSize: '0.85rem',
                                    backgroundColor: theme?.colors?.surface || '#fff',
                                    color: theme?.colors?.text || '#000'
                                }}
                            />
                            <button
                                onClick={() => handleDeleteArea(area)}
                                title={t('deleteArea')}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: theme?.colors?.error || '#d32f2f',
                                    color: theme?.colors?.textLight || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t('delete')}
                            </button>
                        </div>
                    ))}

                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                            type="text"
                            value={newAreaName}
                            maxLength={100}
                            onChange={(e) => setNewAreaName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddArea(); }}
                            placeholder={t('areaName')}
                            style={{
                                flex: '1 1 140px',
                                minWidth: 0,
                                padding: '0.3rem 0.4rem',
                                border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                                borderRadius: theme?.borderRadius || 4,
                                fontSize: '0.85rem',
                                backgroundColor: theme?.colors?.surface || '#fff',
                                color: theme?.colors?.text || '#000'
                            }}
                        />
                        <button
                            onClick={handleAddArea}
                            disabled={saving || !newAreaName.trim()}
                            style={{
                                padding: '0.3rem 0.75rem',
                                backgroundColor: theme?.colors?.primary || '#1976d2',
                                color: theme?.colors?.textLight || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 4,
                                cursor: (saving || !newAreaName.trim()) ? 'not-allowed' : 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                opacity: (saving || !newAreaName.trim()) ? 0.6 : 1
                            }}
                        >
                            {saving ? t('saving') : `+ ${t('addArea')}`}
                        </button>
                    </div>
                </div>
            )}

            {error && (
                <div style={{
                    marginTop: '0.5rem',
                    fontSize: '0.8rem',
                    color: theme?.colors?.error || '#f44336'
                }}>
                    {error}
                </div>
            )}
        </div>
    );
}

export default TableAreasManagement;
